import clsx from 'clsx';
import * as React from 'react';
import { Tooltip } from '../index';

const isCut = (element: Element) => element.scrollWidth > element.clientWidth;

/**
 * Keeps its content on a single line, cuts it with an ellipsis when it
 * overflows, and shows the same content in a tooltip only when it was cut.
 */
export const TruncatedContent = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    // The tooltip clones its child, so the truncated element is read through
    // the wrapper rather than through a ref of its own.
    // Nested blocks and flex items clip their own text, so any of them can be
    // the one cut. Inline elements report 0 for both widths and never count.
    const measure = () => {
      const content = wrapper.firstElementChild;
      setIsTruncated(
        !!content &&
          (isCut(content) ||
            Array.from(content.querySelectorAll('*')).some(isCut)),
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div ref={wrapperRef} className={clsx('mw-100', className)}>
      <Tooltip
        label={children}
        enabled={isTruncated}
        className='truncated-content-tooltip'
      >
        <div className='text-truncate truncated-content-text'>{children}</div>
      </Tooltip>
    </div>
  );
};
